import type { JSONContent } from '@tiptap/core';

import type { TContainerElement } from '@/containerElement';
import type { THeadingElement } from '@/headingElement';
import type { TImageElement } from '@/imageElement';
import type { TParagraphElement } from '@/paragraphElement';

import { BUILD_RSS_FEED_DEFAULT_CARD_STRUCTURE } from '../rssFeedElement/constants';
import type { TRSSFeedAttributes } from '../rssFeedElement/RSSFeed.types';
import { deepClone } from './deepClone';

type NodeVisitor<T = JSONContent> = (node: T) => void;

/**
 * Recursively walks a Tiptap JSONContent tree and calls the visitor on each node.
 */
function walkNodes<T>(node: T, visitor: NodeVisitor<T>): void {
  visitor(node);
  if (typeof node === 'object' && node !== null && 'content' in node && Array.isArray(node.content)) {
    for (const child of node.content) {
      walkNodes(child, visitor);
    }
  }
}

/**
 * Finds a node in the tree by its `lp_name` attribute.
 */
function findNodeByLpName<T>(root: TRSSFeedAttributes['structureTiptapState'], lpName: string): T | undefined {
  let found: T | undefined;
  walkNodes<T>(root as T, (node) => {
    if (
      typeof node === 'object' &&
      node !== null &&
      'attrs' in node &&
      typeof node.attrs === 'object' &&
      node.attrs !== null &&
      'lp_name' in node.attrs &&
      node.attrs.lp_name === lpName
    ) {
      found = node;
    }
  });
  return found;
}

/**
 * Finds a node in the tree by its `mergeTag` attribute.
 */
function findNodeByMergeTag<T>(root: TRSSFeedAttributes['structureTiptapState'], mergeTag: string): T | undefined {
  let found: T | undefined;
  walkNodes<T>(root as T, (node) => {
    if (
      typeof node === 'object' &&
      node !== null &&
      'attrs' in node &&
      typeof node.attrs === 'object' &&
      node.attrs !== null &&
      'mergeTag' in node.attrs &&
      node.attrs.mergeTag === mergeTag
    ) {
      found = node;
    }
  });
  return found;
}

/**
 * Gets the first text mark attrs from a node's content.
 * Returns the attrs object for direct mutation.
 */
function getFirstTextMarkAttrs<T = JSONContent>(node: T): Record<string, unknown> | undefined {
  const textNode =
    typeof node === 'object' && node !== null && 'content' in node && Array.isArray(node.content)
      ? node.content[0]
      : undefined;
  if (!textNode) return undefined;

  if (!textNode.marks || textNode.marks.length === 0) return undefined;

  return textNode.marks[0].attrs;
}

/**
 * Formats a border radius value to the 4-corner format expected by the image node.
 * E.g. '8px' -> '8px 8px 8px 8px'
 */
function formatBorderRadius(value: string): string {
  const trimmed = value.trim();
  if (trimmed.includes(' ')) return trimmed;
  return `${trimmed} ${trimmed} ${trimmed} ${trimmed}`;
}

/**
 * Converts a numeric font size to a CSS px string.
 */
const PX_STRING_REGEX = /^(\d+(\.\d+)?)px$/;
function toFontSizePx(size: number | string): string {
  if (typeof size === 'string' && PX_STRING_REGEX.test(size)) return size;
  return `${size}px`;
}

/**
 * Takes existing RSS feed flat attributes and applies them to a card structure
 * (Tiptap JSONContent), producing a `structureTiptapState` that visually matches
 * what the user had configured with the old attribute-based system.
 *
 * This enables migration from flat attributes to the new card editor system
 * without changing the visual appearance for existing users.
 */
export function applyRSSFeedAttributesToCardStructure(
  attrs: Partial<TRSSFeedAttributes>,
  baseStructure?: TRSSFeedAttributes['structureTiptapState'],
): JSONContent {
  const aspectRatio = attrs.source === 'youtube' ? 1.7777777777777777 : 1;
  const structure = deepClone(
    baseStructure ?? BUILD_RSS_FEED_DEFAULT_CARD_STRUCTURE({ imageAspectRatio: aspectRatio }),
  );

  // --- Outer container (Podcast Card) ---
  const cardContainer = findNodeByLpName<TContainerElement>(structure, 'Podcast Card');
  if (cardContainer?.attrs) {
    if (attrs.backgroundColor !== undefined) {
      cardContainer.attrs.backgroundColor = attrs.backgroundColor;
    }
    if (attrs.borderRadius !== undefined) {
      cardContainer.attrs.borderRadius = attrs.borderRadius;
    }
    if (attrs.borderWidth !== undefined) {
      cardContainer.attrs.borderWidth = attrs.borderWidth;
    }
    if (attrs.borderColor !== undefined) {
      cardContainer.attrs.borderColor = attrs.borderColor;
    }
    if (attrs.borderStyle !== undefined) {
      cardContainer.attrs.borderStyle = attrs.borderStyle;
    }
    if (attrs.itemPadding !== undefined) {
      cardContainer.attrs.padding = attrs.itemPadding;
    }
    if (attrs.itemGap !== undefined) {
      cardContainer.attrs.gap = attrs.itemGap;
    }
  }

  // --- Image ---
  const imageNode = findNodeByMergeTag<TImageElement>(structure, 'podcast.episode.image');
  if (imageNode?.attrs) {
    if (attrs.imageBorderRadius !== undefined) {
      imageNode.attrs.borderRadius = formatBorderRadius(attrs.imageBorderRadius as string);
    }
    if (attrs.imageAspectRatio !== undefined) {
      imageNode.attrs.aspectRatio = attrs.imageAspectRatio;
    }
    if (attrs.imageFit !== undefined) {
      imageNode.attrs.objectFit = attrs.imageFit;
    }
  }

  // --- Title heading ---
  const titleNode = findNodeByMergeTag<THeadingElement>(structure, 'podcast.episode.title');
  if (titleNode) {
    if (!titleNode.attrs) {
      titleNode.attrs = {} as THeadingElement['attrs'];
    }

    if (attrs.titleAlignment !== undefined) {
      titleNode.attrs.textAlign = attrs.titleAlignment;
    }
    if (attrs.titleLineClamp !== undefined) {
      titleNode.attrs.lineClampClass = attrs.titleLineClamp;
    }

    const titleMarkAttrs = getFirstTextMarkAttrs(titleNode);
    if (titleMarkAttrs) {
      if (attrs.titleFontSize !== undefined) {
        titleMarkAttrs.fontSize = toFontSizePx(attrs.titleFontSize);
      }
      if (attrs.titleFontFamily !== undefined) {
        titleMarkAttrs.fontFamily = attrs.titleFontFamily;
      }
      if (attrs.titleTextColor !== undefined) {
        titleMarkAttrs.color = attrs.titleTextColor;
      }
      if (attrs.titleFontWeight !== undefined) {
        titleMarkAttrs.fontWeight = attrs.titleFontWeight;
      }
    }
  }

  // --- Date paragraph ---
  const dateNode = findNodeByMergeTag<TParagraphElement>(structure, 'podcast.episode.publish_date');

  if (attrs.hideDate) {
    // Remove the date paragraph from its parent
    const contentContainer = findNodeByLpName<TContainerElement>(structure, 'Content');
    if (contentContainer?.content && dateNode) {
      contentContainer.content = contentContainer.content.filter((child) => child !== dateNode);
    }
  } else if (dateNode) {
    if (!dateNode.attrs) dateNode.attrs = {};

    if (attrs.subtitleAlignment !== undefined) {
      dateNode.attrs.textAlign = attrs.subtitleAlignment;
    }

    const dateMarkAttrs = getFirstTextMarkAttrs(dateNode);
    if (dateMarkAttrs) {
      if (attrs.subtitleFontSize !== undefined) {
        dateMarkAttrs.fontSize = toFontSizePx(attrs.subtitleFontSize);
      }
      if (attrs.subtitleFontFamily !== undefined) {
        dateMarkAttrs.fontFamily = attrs.subtitleFontFamily;
      }
      if (attrs.subtitleTextColor !== undefined) {
        dateMarkAttrs.color = attrs.subtitleTextColor;
      }
      if (attrs.subtitleFontWeight !== undefined) {
        dateMarkAttrs.fontWeight = attrs.subtitleFontWeight;
      }
    }
  }

  // --- Content container (text gap) ---
  const contentContainer = findNodeByLpName<TContainerElement>(structure, 'Content');
  if (contentContainer?.attrs && attrs.textGap !== undefined) {
    contentContainer.attrs.gap = attrs.textGap;
  }

  return structure;
}
